import { useState } from 'react'
import { ChartBar, PencilSimple, Plus, Trash } from '@phosphor-icons/react'
import { useCan } from '@/auth/context'
import { Banner, DataTable, EmptyState, Field, Input, Select, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { useCrops } from '../api/reference'
import { useDeleteYieldCap, useSaveYieldCap, useYieldCaps, type YieldCapRow } from '../api/setup'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { FormDialog } from '../components/FormDialog'
import { ConsolePage } from '../components/Layout'
import { useUrlState } from '../lib/useUrlState'
import { serverProblem } from '../model/serverError'
import {
  createYieldCapBody,
  draftFromYieldCap,
  emptyYieldCapDraft,
  updateYieldCapBody,
  validateYieldCapDraft,
  yieldCapOk,
  type YieldCapDraft,
} from '../model/yieldCapForm'

const LIMIT = 100

const kgText = (v: string | null) =>
  v === null ? null : `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 })} kg`

type Editing = { mode: 'create'; cropId: string } | { mode: 'edit'; row: YieldCapRow }

/**
 * Crop yield caps (season ceiling, F4.10-d / R22): when a farmer's fields carry no yield estimate,
 * his season ceiling is field area × max kg per acre, else the flat cap per farmer. District row
 * first, else the all-districts row; a district row without a flat cap borrows the state one.
 */
export function YieldCapsPage() {
  const [f, setF, { reset, active }] = useUrlState({ cropId: '', page: 1 })
  const q = useYieldCaps({ page: f.page, limit: LIMIT, cropId: f.cropId || undefined })
  const crops = useCrops()
  const can = useCan()
  const [editing, setEditing] = useState<Editing | null>(null)
  const [removing, setRemoving] = useState<YieldCapRow | null>(null)
  const rows = q.data?.rows ?? []

  const columns: Column<YieldCapRow>[] = [
    { id: 'crop', header: 'Crop', cell: (r) => <span className="font-semibold">{r.cropName ?? '—'}</span>, sortValue: (r) => r.cropName },
    {
      id: 'district',
      header: 'District',
      cell: (r) => r.district ?? <span className="text-muted">All districts</span>,
      sortValue: (r) => r.district ?? '',
    },
    {
      id: 'perAcre',
      header: 'Most per acre',
      align: 'right',
      mono: true,
      cell: (r) => kgText(r.maxKgPerAcre),
      sortValue: (r) => Number(r.maxKgPerAcre),
    },
    {
      id: 'flat',
      header: 'Flat cap per farmer, season',
      align: 'right',
      mono: true,
      cell: (r) => kgText(r.flatCapKgPerFarmerSeason) ?? <span className="font-sans text-muted">No flat cap</span>,
      hideBelow: 'sm',
    },
    { id: 'source', header: 'Source', cell: (r) => <span className="text-muted">{r.source}</span>, hideBelow: 'md' },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <ActionButton perm="crop-yield-caps:update" hide size="sm" variant="ghost" leadingIcon={<PencilSimple />} onClick={() => setEditing({ mode: 'edit', row: r })}>
            Edit
          </ActionButton>
          <ActionButton perm="crop-yield-caps:delete" hide size="sm" variant="ghost" leadingIcon={<Trash />} onClick={() => setRemoving(r)}>
            Delete
          </ActionButton>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Setup"
      title="Crop yield caps"
      description="How much of a crop a farmer can sell in a season when his fields have no yield estimate: field area × the most per acre, or the flat limit per farmer when the area is not known. A district's own cap is used where there is one, otherwise the cap for all districts. Good-stream lots over the limit are held for review."
      actions={
        <ActionButton perm="crop-yield-caps:create" leadingIcon={<Plus />} onClick={() => setEditing({ mode: 'create', cropId: f.cropId })}>
          Add cap
        </ActionButton>
      }
    >
      <DataTable<YieldCapRow>
        caption="Crop yield caps"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={can('crop-yield-caps:update') ? (r) => setEditing({ mode: 'edit', row: r }) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        empty={
          <EmptyState
            icon={<ChartBar />}
            title={active ? 'No caps for this crop' : 'No yield caps yet'}
            description="Add a cap for all districts first, then district caps where yields differ."
            compact
          />
        }
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Crop"
              value={f.cropId}
              onChange={(cropId) => setF({ cropId })}
              options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </FilterBar>
        }
      />
      {editing && <YieldCapDialog editing={editing} crops={crops.data ?? []} onClose={() => setEditing(null)} />}
      <ConfirmDelete row={removing} onClose={() => setRemoving(null)} />
    </ConsolePage>
  )
}

function YieldCapDialog({
  editing,
  crops,
  onClose,
}: {
  editing: Editing
  crops: ReadonlyArray<{ id: string; name: string }>
  onClose: () => void
}) {
  const edit = editing.mode === 'edit' ? editing.row : null
  const [d, setD] = useState<YieldCapDraft>(edit ? draftFromYieldCap(edit) : { ...emptyYieldCapDraft(), cropId: editing.mode === 'create' ? editing.cropId : '' })
  const [touched, setTouched] = useState<Set<keyof YieldCapDraft>>(new Set())
  const save = useSaveYieldCap({ inline: true })
  const p = validateYieldCapDraft(d, editing.mode)
  const touch = (k: keyof YieldCapDraft) => setTouched((s) => new Set(s).add(k))
  const shown = (k: keyof YieldCapDraft) => (touched.has(k) ? p[k] : undefined)
  const err = save.isError ? serverProblem(save.error) : null

  return (
    <FormDialog
      open
      onClose={onClose}
      title={edit ? `Edit cap · ${edit.cropName ?? 'crop'}, ${edit.district ?? 'all districts'}` : 'Add yield cap'}
      saveLabel={edit ? 'Save' : 'Add cap'}
      loading={save.isPending}
      canSave={yieldCapOk(p)}
      onSave={() => save.mutateAsync(edit ? { id: edit.id, body: updateYieldCapBody(d) } : { body: createYieldCapBody(d) })}
    >
      {err && (
        <Banner tone="error" title={err.status === 409 ? 'This cap already exists' : err.message}>
          {err.status === 409
            ? `${err.message}. Edit the existing cap instead.`
            : err.details.join(' · ')}
        </Banner>
      )}
      {edit ? (
        <p className="text-[14px] text-muted">
          Crop and district cannot change. To cap another district, add a new cap.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Crop" required error={shown('cropId')}>
            <Select
              value={d.cropId}
              onChange={(cropId) => {
                setD({ ...d, cropId })
                touch('cropId')
              }}
              onBlur={() => touch('cropId')}
              placeholder="Choose…"
              options={crops.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
          <Field label="District" error={shown('district')} hint="Leave empty for all districts">
            <Input value={d.district} onChange={(e) => setD({ ...d, district: e.target.value })} onBlur={() => touch('district')} maxLength={60} placeholder="All districts" />
          </Field>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Most kg per acre" required error={shown('maxKgPerAcre')}>
          <Input
            inputMode="decimal"
            value={d.maxKgPerAcre}
            onChange={(e) => setD({ ...d, maxKgPerAcre: e.target.value })}
            onBlur={() => touch('maxKgPerAcre')}
            trailing="kg / acre"
            placeholder="1200"
            className="font-mono"
            data-autofocus={edit ? true : undefined}
          />
        </Field>
        <Field label="Flat cap per farmer per season" error={shown('flatCap')} hint={d.district.trim() || edit?.district ? 'Used when the field area is not known. Empty: the all-districts flat cap applies.' : 'Used when the field area is not known. Optional.'}>
          <Input
            inputMode="decimal"
            value={d.flatCap}
            onChange={(e) => setD({ ...d, flatCap: e.target.value })}
            onBlur={() => touch('flatCap')}
            trailing="kg"
            placeholder="2500"
            className="font-mono"
          />
        </Field>
      </div>
      <Field label="Source" required error={shown('source')} hint="Where the figure comes from, e.g. agriculture department yield survey 2025">
        <Input value={d.source} onChange={(e) => setD({ ...d, source: e.target.value })} onBlur={() => touch('source')} maxLength={120} />
      </Field>
    </FormDialog>
  )
}

function ConfirmDelete({ row, onClose }: { row: YieldCapRow | null; onClose: () => void }) {
  const del = useDeleteYieldCap()
  return (
    <ConfirmDialog
      open={Boolean(row)}
      onClose={onClose}
      title={`Delete the ${row?.cropName ?? ''} cap for ${row?.district ?? 'all districts'}?`}
      description={
        row?.district
          ? 'Farmers in this district fall back to the cap for all districts.'
          : 'Farmers in districts without their own cap will have no limit for this crop, and their good-stream lots are held until one is added.'
      }
      confirmLabel="Delete cap"
      tone="danger"
      loading={del.isPending}
      onConfirm={async () => {
        if (!row) return
        await del.mutateAsync(row.id)
        onClose()
      }}
    />
  )
}
